import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const methodologySteps = [
  {
    title: 'Elaboração ou atualização do modelo de negócio (BMC)',
    description:
      'Revisamos a proposta de valor, segmentos, canais, receitas e operação para alinhar a transformação digital ao modelo real da empresa.',
  },
  {
    title: 'Identificação de gargalos',
    description:
      'Mapeamos processos, retrabalhos, pontos de espera e perdas operacionais para priorizar o que mais trava crescimento, eficiência e previsibilidade.',
  },
  {
    title: 'Verificação de potencial uso de automações ou IA',
    description:
      'Avaliamos onde automação, integrações e inteligência artificial podem reduzir esforço manual, acelerar decisão e ampliar capacidade operacional.',
  },
  {
    title: 'Definição de métricas',
    description:
      'Estabelecemos indicadores objetivos para acompanhar eficiência, qualidade, tempo, custo e impacto no negócio antes e depois da implementação.',
  },
  {
    title: 'Proposta de solução',
    description:
      'Consolidamos diagnóstico, prioridades e oportunidades em uma proposta viável, com escopo claro, etapas, riscos e ganhos esperados.',
  },
  {
    title: 'Implementação',
    description:
      'Desenvolvemos, configuramos e integramos a solução escolhida com foco em robustez técnica, aderência ao processo e velocidade de entrega.',
  },
  {
    title: 'Aplicação',
    description:
      'Colocamos a solução em operação com acompanhamento próximo, ajustando fluxos, treinamento e uso real no contexto do time.',
  },
  {
    title: 'Validação com as métricas',
    description:
      'Comparamos os resultados com os indicadores definidos para verificar ganhos concretos, desvios e oportunidades de refinamento.',
  },
  {
    title: 'Conclusão ou iteração',
    description:
      'Encerramos o ciclo com aprendizados consolidados ou iniciamos uma nova iteração para ampliar impacto, maturidade e retorno.',
  },
];

export default function MethodologySection() {
  const { ref, visible } = useScrollAnimation();

  return (
    <section id="methodology" className="py-20 md:py-28 bg-zinc-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div
          ref={ref}
          className={`text-center mb-14 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="inline-block text-emerald-600 font-semibold text-sm font-inter tracking-widest uppercase mb-3">
            Nosso processo
          </span>
          <h2 className="font-sora font-bold text-3xl md:text-4xl text-zinc-900 mb-4">
            Como conduzimos a transformação
          </h2>
          <p className="font-inter text-zinc-500 max-w-3xl mx-auto text-base leading-relaxed">
            Cada projeto segue uma jornada estruturada, do entendimento do modelo de negócio à validação dos resultados. Assim,
            conectamos estratégia, execução e melhoria contínua em iniciativas de software, automação e IA.
          </p>
          <div className="w-12 h-1 bg-emerald-500 rounded-full mx-auto mt-5" />
        </div>

        <ol className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {methodologySteps.map((step, index) => (
            <li
              key={step.title}
              className={`rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm transition-all duration-500 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: visible ? `${index * 70}ms` : '0ms' }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 shrink-0 rounded-xl bg-emerald-500 text-white flex items-center justify-center font-sora font-bold text-lg">
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div>
                  <h3 className="font-sora font-semibold text-zinc-900 text-lg leading-snug mb-2">{step.title}</h3>
                  <p className="font-inter text-zinc-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}